import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { trackEvent } from '../../utils/analytics';
import { loadChemistryVideoAssets, waitForChemistryVideoFonts } from './video/chemistryVideoAssets';
import {
  CHEMISTRY_VIDEO_DURATION_MS,
  CHEMISTRY_VIDEO_FPS,
  CHEMISTRY_VIDEO_HEIGHT,
  CHEMISTRY_VIDEO_WIDTH,
} from './video/chemistryVideoLayout';
import { recordChemistryCanvasVideo } from './video/chemistryVideoRecorder';
import { createChemistryVideoRenderModel, renderChemistryVideoFrame } from './video/chemistryVideoRenderer';
import { ChemistryVideoError, clearCanvas } from './video/chemistryVideoUtils';
import { useChemistryMp4 } from './video/useChemistryMp4';
import './ChemistryVideo.css';

const ERROR_MESSAGE_KEYS = {
  asset_load_failed: 'asset_error',
  font_load_failed: 'font_error',
  canvas_context_missing: 'browser_error',
  media_recorder_unsupported: 'unsupported_error',
  media_recorder_failed: 'generation_error',
  empty_video_blob: 'empty_error',
  render_failed: 'generation_error',
};

function getErrorCode(error) {
  if (error instanceof ChemistryVideoError) return error.code;
  return 'media_recorder_failed';
}

export default function ChemistryVideoGenerator({
  result,
  imageA,
  imageB,
  meshImageA,
  meshImageB,
  nameA,
  nameB,
  language,
  shareText,
}) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const previewUrlRef = useRef('');
  const abortControllerRef = useRef(null);
  const mountedRef = useRef(true);
  const previewTrackedRef = useRef(false);
  const downloadUrlsRef = useRef(new Set());

  const [videoBlob, setVideoBlob] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState('');
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [videoGenerationError, setVideoGenerationError] = useState('');
  const [videoProgress, setVideoProgress] = useState(0);
  const [isSharingVideo, setIsSharingVideo] = useState(false);
  const [videoShareError, setVideoShareError] = useState('');
  const [videoSaveError, setVideoSaveError] = useState('');
  const [videoShareNotice, setVideoShareNotice] = useState('');
  const {
    conversionStatus, conversionError, mp4VideoUrl, mp4ExpiresAt, mp4File, mp4Blob,
    convertToMp4, resetMp4,
  } = useChemistryMp4();
  const preferredVideoFile = mp4File || videoFile;

  const tx = useCallback((key, defaultValue, options = {}) => t(`chemistry_result.video.${key}`, {
    defaultValue,
    ...options,
  }), [t]);
  const isEnglish = String(language).startsWith('en');
  const labels = useMemo(() => ({
    brandTitle: tx('canvas.brand_title', 'STARTUS FACE CHEMISTRY'),
    faceSignal: tx('canvas.face_signal', isEnglish ? 'Reading visual face signals' : '얼굴의 시각적 신호를 읽는 중'),
    compareTitle: tx('canvas.compare_title', isEnglish ? 'Two chemistry signals' : '두 사람의 궁합 신호'),
    outroPrompt: tx('canvas.outro_prompt', isEnglish ? 'What would your score be?' : '너희 둘은 몇 점일까?'),
  }), [isEnglish, tx]);

  const canShareVideo = useMemo(() => {
    if (!preferredVideoFile || typeof navigator === 'undefined') return false;
    if (typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') return false;
    try {
      return navigator.canShare({ files: [preferredVideoFile] });
    } catch {
      return false;
    }
  }, [preferredVideoFile]);

  const revokePreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
    setVideoPreviewUrl('');
  }, []);

  const generateVideo = useCallback(async () => {
    if (isGeneratingVideo) return;
    abortControllerRef.current?.abort();
    revokePreviewUrl();
    resetMp4();
    clearCanvas(canvasRef.current);
    setVideoBlob(null);
    setVideoFile(null);
    setVideoGenerationError('');
    setVideoShareError('');
    setVideoSaveError('');
    setVideoShareNotice('');
    setVideoProgress(0);
    setIsGeneratingVideo(true);
    previewTrackedRef.current = false;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    trackEvent('video_creation_started', {
      analysis_type: 'chemistry',
      source_page: 'chemistry_result',
    });
    try {
      await waitForChemistryVideoFonts();
      const assets = await loadChemistryVideoAssets({
        imageA,
        imageB,
        meshImageA,
        meshImageB,
      });
      if (abortController.signal.aborted) throw new DOMException('Video generation aborted', 'AbortError');
      const model = createChemistryVideoRenderModel({ result, nameA, nameB, language, t, labels });
      const recording = await recordChemistryCanvasVideo({
        canvas: canvasRef.current,
        durationMs: CHEMISTRY_VIDEO_DURATION_MS,
        fps: CHEMISTRY_VIDEO_FPS,
        signal: abortController.signal,
        onProgress: (nextProgress) => {
          if (!mountedRef.current) return;
          setVideoProgress((current) => current === nextProgress ? current : nextProgress);
        },
        renderFrame: (ctx, elapsedMs) => renderChemistryVideoFrame(ctx, elapsedMs, { assets, model }),
      });
      if (!mountedRef.current || abortController.signal.aborted) return;
      const file = new File([recording.blob], `startus-chemistry-${Date.now()}.webm`, {
        type: recording.blob.type,
      });
      const previewUrl = URL.createObjectURL(recording.blob);
      previewUrlRef.current = previewUrl;
      setVideoBlob(recording.blob);
      setVideoFile(file);
      setVideoPreviewUrl(previewUrl);
      clearCanvas(canvasRef.current);
      trackEvent('video_creation_completed', {
        analysis_type: 'chemistry',
        mime_type: recording.blob.type,
        duration_ms: recording.durationMs,
      });
      void convertToMp4(file);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      const errorCode = getErrorCode(error);
      console.error(`[chemistry-video] ${errorCode}`, error);
      if (mountedRef.current) {
        const messageKey = ERROR_MESSAGE_KEYS[errorCode] || 'generation_error';
        setVideoGenerationError(tx(
          `errors.${messageKey}`,
          isEnglish
            ? 'The video could not be created. Please try again.'
            : '영상을 만들지 못했어요. 잠시 후 다시 시도해 주세요.',
        ));
      }
      trackEvent('video_creation_failed', {
        analysis_type: 'chemistry',
        failure_reason: errorCode,
      });
    } finally {
      if (mountedRef.current && abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
        setIsGeneratingVideo(false);
      }
    }
  }, [convertToMp4, imageA, imageB, isEnglish, isGeneratingVideo, labels, language, meshImageA, meshImageB, nameA, nameB, resetMp4, result, revokePreviewUrl, t, tx]);

  const handleGenerate = useCallback(() => {
    trackEvent('result_action_clicked', {
      analysis_type: 'chemistry',
      action_name: 'create_video',
      source_section: 'recommendation',
    });
    trackEvent('video_creation_clicked', {
      analysis_type: 'chemistry',
      source_page: 'chemistry_result',
    });
    generateVideo();
  }, [generateVideo]);

  const handleRegenerate = useCallback(() => {
    handleGenerate();
  }, [handleGenerate]);

  const handleShareVideo = useCallback(async () => {
    if (!preferredVideoFile) return;
    trackEvent('result_action_clicked', {
      analysis_type: 'chemistry',
      action_name: 'share',
      source_section: 'video',
    });
    try {
      setIsSharingVideo(true);
      setVideoShareError('');
      setVideoShareNotice('');
      const canShareFiles = typeof navigator.share === 'function'
        && typeof navigator.canShare === 'function'
        && navigator.canShare({ files: [preferredVideoFile] });
      if (!canShareFiles) throw new Error('video_file_share_unavailable');
      await navigator.share({
        title: 'STARTUS FACE CHEMISTRY',
        text: shareText,
        files: [preferredVideoFile],
      });
      setVideoShareNotice(tx(
        'share.opened',
        isEnglish ? 'The video share sheet is open.' : '영상 공유창을 열었어요.',
      ));
      trackEvent('share_completed', {
        analysis_type: 'chemistry',
        share_method: 'native_share',
      });
      if (mp4File) trackEvent('chemistry_video_mp4_share', {
        analysis_type: 'chemistry', share_method: 'native_share',
      });
    } catch (error) {
      const name = String(error?.name || '').toLowerCase();
      const message = String(error?.message || '').toLowerCase();
      const cancelled = name.includes('abort') || message.includes('abort') || message.includes('cancel');
      if (cancelled) {
        setVideoShareNotice(tx(
          'share.cancelled',
          isEnglish ? 'Video sharing was cancelled.' : '영상 공유를 취소했어요.',
        ));
        return;
      }
      console.error('[chemistry-video] share failed', error);
      setVideoShareError(tx(
        'share.failed',
        isEnglish
          ? 'Sharing could not be opened. Use the save video button.'
          : '공유를 열지 못했어요. 영상 저장 버튼을 이용해 주세요.',
      ));
    } finally {
      setIsSharingVideo(false);
    }
  }, [isEnglish, mp4File, preferredVideoFile, shareText, tx]);

  const handleSaveVideo = useCallback(() => {
    const preferredBlob = mp4Blob || videoBlob;
    if (!preferredBlob) return;
    trackEvent('result_action_clicked', {
      analysis_type: 'chemistry',
      action_name: 'download',
      source_section: 'video',
    });
    let url = '';
    try {
      setVideoSaveError('');
      const extension = preferredBlob.type.includes('mp4') ? 'mp4' : 'webm';
      url = URL.createObjectURL(preferredBlob);
      downloadUrlsRef.current.add(url);
      const link = document.createElement('a');
      link.href = url;
      link.download = `startus-chemistry-${Date.now()}.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => {
        URL.revokeObjectURL(url);
        downloadUrlsRef.current.delete(url);
      }, 1000);
      if (mp4Blob) trackEvent('chemistry_video_mp4_download', { analysis_type: 'chemistry' });
    } catch (error) {
      if (url) {
        URL.revokeObjectURL(url);
        downloadUrlsRef.current.delete(url);
      }
      console.error('[chemistry-video] save failed', error);
      setVideoSaveError(tx(
        'save.failed',
        isEnglish ? 'The video could not be saved. Please try again.' : '영상을 저장하지 못했어요. 다시 시도해 주세요.',
      ));
    }
  }, [isEnglish, mp4Blob, tx, videoBlob]);

  const handlePreviewPlay = useCallback(() => {
    if (previewTrackedRef.current) return;
    previewTrackedRef.current = true;
    trackEvent('report_section_viewed', {
      analysis_type: 'chemistry',
      section_name: 'video_preview',
    });
    if (mp4VideoUrl) trackEvent('chemistry_video_mp4_preview', { analysis_type: 'chemistry' });
  }, [mp4VideoUrl]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const downloadUrls = downloadUrlsRef.current;
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
      downloadUrls.forEach((url) => URL.revokeObjectURL(url));
      downloadUrls.clear();
      clearCanvas(canvas);
    };
  }, []);

  const buttonLabel = isGeneratingVideo
    ? tx('generating', isEnglish ? 'Creating video {{progress}}%' : '영상 생성 중 {{progress}}%', { progress: videoProgress })
    : tx('generate', isEnglish ? 'Create TikTok video' : '틱톡 영상 만들기');

  return (
    <section className="chem-video-generator" aria-labelledby="chem-video-title">
      <div className="chem-video-heading">
        <span>{tx('eyebrow', isEnglish ? 'Vertical chemistry video' : '세로형 궁합 영상')}</span>
        <h2 id="chem-video-title">{tx('title', isEnglish ? 'Turn this result into a video' : '이 결과를 영상으로 만들기')}</h2>
        <p>{tx('description', isEnglish ? 'A 9:16 preview is created only in this browser.' : '9:16 영상을 이 브라우저 안에서만 생성합니다.')}</p>
      </div>

      <div className={`chem-video-stage ${videoPreviewUrl ? 'is-ready' : ''}`}>
        <canvas
          ref={canvasRef}
          width={CHEMISTRY_VIDEO_WIDTH}
          height={CHEMISTRY_VIDEO_HEIGHT}
          aria-label={tx('canvas_label', isEnglish ? 'Chemistry video rendering canvas' : '궁합 영상 렌더링 캔버스')}
        />
        {videoPreviewUrl ? (
          <video
            src={mp4VideoUrl || videoPreviewUrl}
            controls
            playsInline
            muted
            loop
            preload="metadata"
            onPlay={handlePreviewPlay}
            data-blob-size={videoBlob?.size || 0}
            data-file-name={videoFile?.name || ''}
          />
        ) : (
          <div className="chem-video-placeholder" aria-hidden="true">
            <strong>9:16</strong>
            <span>720 × 1280</span>
          </div>
        )}
      </div>

      {isGeneratingVideo ? (
        <div className="chem-video-progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={videoProgress}>
          <span style={{ width: `${videoProgress}%` }} />
        </div>
      ) : null}
      {conversionStatus === 'uploading' || conversionStatus === 'converting' ? (
        <p className="chem-video-notice" role="status">
          {tx('conversion.progress', isEnglish ? 'Converting to MP4…' : 'MP4 영상으로 변환 중...')}
        </p>
      ) : null}
      {conversionStatus === 'failed' ? (
        <p className="chem-video-error" role="alert" data-error-code={conversionError}>
          {tx('conversion.failed', isEnglish ? 'MP4 conversion failed. You can still save the WebM video.' : 'MP4 변환에 실패했어요. WebM 영상은 계속 저장할 수 있습니다.')}
        </p>
      ) : null}
      {conversionStatus === 'ready' ? (
        <p className="chem-video-notice" role="status" data-expires-at={mp4ExpiresAt}>
          {tx('conversion.expires', isEnglish ? 'The generated MP4 is available for 24 hours.' : '생성된 MP4 영상은 24시간 동안 보관됩니다.')}
        </p>
      ) : null}
      {videoGenerationError ? <p className="chem-video-error" role="alert">{videoGenerationError}</p> : null}
      {videoShareError ? <p className="chem-video-error" role="alert">{videoShareError}</p> : null}
      {videoSaveError ? <p className="chem-video-error" role="alert">{videoSaveError}</p> : null}
      {videoShareNotice ? <p className="chem-video-notice" role="status">{videoShareNotice}</p> : null}
      {videoPreviewUrl ? <strong className="chem-video-preview-label">{tx('preview', isEnglish ? 'Video preview' : '영상 미리보기')}</strong> : null}

      {videoPreviewUrl ? (
        <div className="chem-video-actions">
          <button type="button" className="chem-video-button" onClick={handleShareVideo} disabled={!canShareVideo || isSharingVideo}>
            {isSharingVideo
              ? tx('share.preparing', isEnglish ? 'Preparing to share…' : '공유 준비 중...')
              : tx('share.button', isEnglish ? 'Share video' : '영상 공유')}
          </button>
          <button type="button" className="chem-video-button chem-video-button-secondary" onClick={handleSaveVideo}>
            {tx('save.button', isEnglish ? 'Save video' : '영상 저장')}
          </button>
          <button type="button" className="chem-video-button chem-video-button-ghost" onClick={handleRegenerate} disabled={isSharingVideo}>
            {tx('regenerate', isEnglish ? 'Create again' : '다시 만들기')}
          </button>
        </div>
      ) : (
        <button type="button" className="chem-video-button" onClick={handleGenerate} disabled={isGeneratingVideo}>
          {buttonLabel}
        </button>
      )}
      {videoPreviewUrl && !canShareVideo ? (
        <p className="chem-video-support-note">
          {tx(
            'share.unsupported',
            isEnglish
              ? 'File sharing is unavailable in this browser. You can still save the video.'
              : '이 브라우저에서는 파일 공유를 지원하지 않아요. 영상 저장은 사용할 수 있어요.',
          )}
        </p>
      ) : null}
    </section>
  );
}

ChemistryVideoGenerator.propTypes = {
  result: PropTypes.object.isRequired,
  imageA: PropTypes.string,
  imageB: PropTypes.string,
  meshImageA: PropTypes.string,
  meshImageB: PropTypes.string,
  nameA: PropTypes.string.isRequired,
  nameB: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  shareText: PropTypes.string.isRequired,
};
